import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MessageCircle, MapPin, Clock } from "lucide-react";

import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { marketplaceApi, Product } from "@/services/marketplace";

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCartModal, setShowCartModal] = useState(false);

  useEffect(() => {
    const fetchProductData = async () => {
      if (!id) {
        navigate("/marketplace");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Convert id to number
        const productId = parseInt(id, 10);
        if (isNaN(productId)) {
          throw new Error("Invalid product ID");
        }

        // Fetch product details
        const productData = await marketplaceApi.getProduct(productId);
        setProduct(productData);

        // Fetch related products
        const relatedProductsData =
          await marketplaceApi.getRelatedProducts(productId);
        setRelatedProducts(relatedProductsData);
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Không thể tải thông tin sản phẩm. Vui lòng thử lại sau.");
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [id, navigate]);

  // Set document title
  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Locuno Marketplace`;
    }
  }, [product]);

  const handleAddToCart = () => {
    setShowCartModal(true);
    setTimeout(() => {
      setShowCartModal(false);
    }, 2000);
  };

  const handleContactSeller = () => {
    // Implement contact seller functionality
    console.log("Contact seller for product:", product?.id);
  };

  // Format price with commas and đ
  const formatPrice = (value: string) => {
    // Remove non-numeric characters and parse as number
    const numericPrice = parseFloat(value.replace(/[^\d]/g, ""));
    // Format with dots as thousand separators and add đ
    return numericPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "đ";
  };

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#E06448]"></div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error || !product) {
    return (
      <AuthenticatedLayout>
        <div className="p-4">
          <div className="flex items-center p-4 bg-red-50 rounded-lg">
            <div className="text-red-700 text-sm">
              {error || "Không tìm thấy sản phẩm"}
            </div>
          </div>
          <button
            className="mt-4 bg-[#E06448] text-white px-4 py-2 rounded-full"
            onClick={() => navigate("/marketplace")}
          >
            Quay lại trang chợ
          </button>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout backgroundColor="#FBF6F1">
      <div className="pb-20">
        {/* Product Image with integrated navigation */}
        <div className="relative">
          <div className="w-full h-[300px] bg-[#FBF6F1] relative">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Product Info */}
        <div className="w-full p-4 flex flex-col justify-start items-start gap-3">
          <div className="self-stretch justify-start text-[#1E3A3A] text-base font-normal font-sora leading-tight">
            {product.name}
          </div>
        </div>

        {/* Seller Info */}
        <div className="w-full p-4 border-t-[0.30px] border-[#D1D1D0] inline-flex justify-between items-center">
          <div className="flex justify-start items-center gap-3">
            <div data-avatar="1" data-size="small" className="size-9 relative">
              <img
                className="size-9 left-0 top-0 absolute rounded-full"
                src="/avatars/kiki.svg"
                alt="Seller"
              />
            </div>
            <div className="inline-flex flex-col justify-center items-start gap-0.5">
              <div className="inline-flex justify-start items-end gap-1.5">
                <div className="justify-start text-[#1E3A3A] text-sm font-medium font-sora leading-none">
                  Kiki
                </div>
              </div>
              <div className="justify-start text-[#6F6F6B] text-[10px] font-normal font-sora leading-none">
                Thành viên từ tháng 5, 2025
              </div>
            </div>
          </div>
          <button
            onClick={handleContactSeller}
            data-hierarchy="disable"
            data-show-icon-line-left="true"
            data-show-icon-line-right="false"
            data-size="small"
            className="px-4 py-2.5 bg-[#E7E7E6] rounded-full flex justify-center items-center gap-2.5"
          >
            <div className="size-5 relative overflow-hidden">
              <MessageCircle className="h-5 w-5 text-[#1E3A3A]" />
            </div>
            <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">
              Nhắn tin
            </div>
          </button>
        </div>

        {/* Product Description */}
        <div className="w-full p-4 border-t-[0.30px] border-[#D1D1D0] flex flex-col justify-start items-start gap-2">
          <div className="justify-start text-[#1E3A3A] text-base font-medium font-sora leading-tight">
            Mô tả
          </div>
          <div className="self-stretch justify-start text-[#6F6F6B] text-sm font-normal font-sora leading-none">
            {product.description ||
              "Định hình và chải mượt, ẩn tóc con chỉ trong 1 lần sấy. Mang lại mái tóc mềm mượt, óng ả. Được thiết kế để chăm sóc tóc và da đầu. Nhanh khô và không gây hư tổn tóc từ nhiệt. Bảo hành 2 năm. Đổi hàng trong 14 ngày."}
          </div>
        </div>

        {/* Safety Tips */}
        <div className="self-stretch p-4 border-t-[0.30px] border-[#D1D1D0] inline-flex justify-start items-center gap-3">
          <div
            data-property-1="security"
            className="p-2 bg-[#F2E3FF] rounded-full flex justify-center items-center gap-2.5"
          >
            <div className="size-5 relative overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.33301 12.9398V2.1665C3.33301 1.89036 3.55687 1.6665 3.83301 1.6665H16.1663C16.4425 1.6665 16.6663 1.89036 16.6663 2.1665V12.9398C16.6663 14.2023 15.953 15.3565 14.8238 15.9212L10.2233 18.2213C10.0825 18.2918 9.91684 18.2918 9.77609 18.2213L5.17563 15.9212C4.04635 15.3565 3.33301 14.2023 3.33301 12.9398Z"
                  stroke="#9C029C"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          <div className="inline-flex flex-col justify-center items-start gap-0.5">
            <div className="inline-flex justify-start items-end gap-1.5">
              <div className="justify-start text-[#1E3A3A] text-sm font-medium font-sora leading-none">
                Safety Tips
              </div>
            </div>
            <div className="justify-start text-[#6F6F6B] text-[10px] font-normal font-sora leading-none">
              Hẹn gặp trao đổi hàng ở nơi công cộng, an toàn
              <br />
              Không thanh toán trước khi nhận và kiểm tra hàng
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="w-full flex flex-col justify-between items-start">
            <div className="px-4 py-2 inline-flex justify-center items-center gap-2.5">
              <div className="justify-start text-[#1E3A3A] text-xl font-medium font-manrope leading-normal">
                Sản phẩm tương tự
              </div>
            </div>
            <div className="self-stretch px-4 pt-4 pb-2 inline-flex justify-start items-center gap-4">
              {relatedProducts.slice(0, 2).map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  className="flex-1 relative bg-[#FBF6F1] rounded-lg shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] inline-flex flex-col justify-start items-center"
                  onClick={() =>
                    navigate(`/marketplace/product/${relatedProduct.id}`)
                  }
                >
                  <img
                    className="self-stretch h-40 rounded-tl-lg rounded-tr-lg object-cover"
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                  />
                  <div className="self-stretch p-2 flex flex-col justify-start items-start gap-1">
                    <div className="justify-start text-[#1E3A3A] text-sm font-medium font-sora leading-none">
                      {relatedProduct.name}
                    </div>
                    <div className="self-stretch justify-start text-[#E06448] text-base font-semibold font-manrope leading-tight">
                      {formatPrice(relatedProduct.price)}
                    </div>
                    <div className="inline-flex justify-start items-center gap-1">
                      <div className="size-3.5 relative overflow-hidden">
                        <MapPin className="h-3.5 w-3.5 text-[#6F6F6B]" />
                      </div>
                      <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                        {relatedProduct.location}
                      </div>
                      <div className="size-0.5 bg-[#6F6F6B] rounded-full"></div>
                      <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                        {relatedProduct.distance}
                      </div>
                    </div>
                    <div className="inline-flex justify-start items-center gap-1">
                      <div className="size-3.5 relative overflow-hidden">
                        <Clock className="h-3.5 w-3.5 text-[#6F6F6B]" />
                      </div>
                      <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                        {relatedProduct.time}
                      </div>
                    </div>
                  </div>
                  {relatedProduct.discount && (
                    <div
                      data-property-1="discount"
                      className="px-3 py-1 absolute top-2 right-2 bg-[#FCE8E4] rounded-full inline-flex justify-center items-center gap-2.5"
                    >
                      <div className="justify-start text-[#E06448] text-[10px] font-normal font-sora leading-none">
                        -{relatedProduct.discount}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {relatedProducts.length > 2 && (
              <div className="self-stretch px-4 py-2 inline-flex justify-start items-center gap-4">
                {relatedProducts.slice(2, 4).map((relatedProduct) => (
                  <div
                    key={relatedProduct.id}
                    className="flex-1 relative bg-[#FBF6F1] rounded-lg shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] inline-flex flex-col justify-start items-center"
                    onClick={() =>
                      navigate(`/marketplace/product/${relatedProduct.id}`)
                    }
                  >
                    <img
                      className="self-stretch h-40 rounded-tl-lg rounded-tr-lg object-cover"
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                    />
                    <div className="self-stretch p-2 flex flex-col justify-start items-start gap-1">
                      <div className="justify-start text-[#1E3A3A] text-sm font-medium font-sora leading-none">
                        {relatedProduct.name}
                      </div>
                      <div className="self-stretch justify-start text-[#E06448] text-base font-semibold font-manrope leading-tight">
                        {formatPrice(relatedProduct.price)}
                      </div>
                      <div className="inline-flex justify-start items-center gap-1">
                        <div className="size-3.5 relative overflow-hidden">
                          <MapPin className="h-3.5 w-3.5 text-[#6F6F6B]" />
                        </div>
                        <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                          {relatedProduct.location}
                        </div>
                        <div className="size-0.5 bg-[#6F6F6B] rounded-full"></div>
                        <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                          {relatedProduct.distance}
                        </div>
                      </div>
                      <div className="inline-flex justify-start items-center gap-1">
                        <div className="size-3.5 relative overflow-hidden">
                          <Clock className="h-3.5 w-3.5 text-[#6F6F6B]" />
                        </div>
                        <div className="justify-start text-[#6F6F6B] text-xs font-normal font-sora leading-none">
                          {relatedProduct.time}
                        </div>
                      </div>
                    </div>
                    {relatedProduct.discount && (
                      <div
                        data-property-1="discount"
                        className="px-3 py-1 absolute top-2 right-2 bg-[#FCE8E4] rounded-full inline-flex justify-center items-center gap-2.5"
                      >
                        <div className="justify-start text-[#E06448] text-[10px] font-normal font-sora leading-none">
                          -{relatedProduct.discount}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#FBF6F1] border-t border-[#D1D1D0] p-3 flex items-center gap-3">
          <button
            className="flex-1 bg-[#E7E7E6] text-[#1E3A3A] py-2.5 rounded-full flex items-center justify-center gap-2"
            onClick={handleContactSeller}
          >
            <MessageCircle className="h-5 w-5" />
            <span className="font-sora">Nhắn tin</span>
          </button>
          <button
            className="flex-1 bg-[#E06448] text-[#F4E7DA] py-2.5 rounded-full font-sora"
            onClick={handleAddToCart}
          >
            Thêm vào giỏ
          </button>
        </div>

        {/* Cart Success Modal */}
        {showCartModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg w-[90%] max-w-sm p-5 animate-slide-up">
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <svg
                    className="h-8 w-8 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-[#1E3A3A] mb-2">
                  Đã thêm vào giỏ hàng!
                </h2>
                <p className="text-[#6F6F6B] text-sm mb-6">
                  Sản phẩm đã được thêm vào giỏ hàng của bạn thành công
                </p>

                <div className="flex flex-col w-full gap-3">
                  <button
                    onClick={() => navigate("/marketplace/cart")}
                    className="w-full bg-[#E06448] text-white py-2.5 rounded-full font-medium"
                  >
                    Xem giỏ hàng
                  </button>
                  <button
                    onClick={() => setShowCartModal(false)}
                    className="w-full bg-white border border-[#E06448] text-[#E06448] py-2.5 rounded-full font-medium"
                  >
                    Tiếp tục mua sắm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};

export default ProductDetailPage;
